import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';

import { useLazyGetClinicDocumentStatusQuery } from '../store';
import type { ClinicDocumentCardResponse } from '../types';

const FAST_POLL_MS = 2000;
const SLOW_POLL_MS = 5000;
const FAST_POLL_WINDOW_MS = 15000;

const PROCESSING_STATUSES = new Set(['uploaded', 'metadata_ready', 'processing']);
const isProcessingStatus = (status: string): boolean => PROCESSING_STATUSES.has(status);
const isTerminalStatus = (status: string): boolean =>
  status === 'processed' || status === 'failed';

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'processed':
      return '#4caf50';
    case 'failed':
      return '#f44336';
    case 'processing':
      return '#ff9800';
    case 'metadata_ready':
      return '#1976d2';
    default:
      return '#bdbdbd';
  }
};

const formatShortDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

interface ClinicDocumentListItemProps {
  document: ClinicDocumentCardResponse;
  isSelected: boolean;
  onClick: () => void;
  onStatusChange: (documentId: string, nextStatus: string, nextTitle: string | null) => void;
}

export function ClinicDocumentListItem({
  document,
  isSelected,
  onClick,
  onStatusChange,
}: ClinicDocumentListItemProps) {
  const [triggerPoll] = useLazyGetClinicDocumentStatusQuery();
  const [effectiveStatus, setEffectiveStatus] = useState(document.processing_status);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartRef = useRef<number>(0);
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    const docId = document.clinic_document_id;
    const initialStatus = document.processing_status;
    setEffectiveStatus(initialStatus);

    if (!isProcessingStatus(initialStatus)) {
      return;
    }

    pollingStartRef.current = Date.now();

    const scheduleNext = () => {
      const elapsed = Date.now() - pollingStartRef.current;
      const interval = elapsed < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
      intervalRef.current = setTimeout(() => void poll(), interval);
    };

    const poll = async () => {
      try {
        const result = await triggerPoll({ clinicDocumentId: docId }).unwrap();
        const nextStatus = result.processing_status;

        setEffectiveStatus(nextStatus);
        onStatusChangeRef.current(docId, nextStatus, result.title ?? null);

        if (isTerminalStatus(nextStatus)) {
          stopPolling();
        } else {
          scheduleNext();
        }
      } catch {
        stopPolling();
      }
    };

    scheduleNext();

    return stopPolling;
  }, [document.clinic_document_id, document.processing_status, triggerPoll, stopPolling]);

  const showSkeleton = document.title === null && effectiveStatus === 'uploaded';
  const isProcessing = isProcessingStatus(effectiveStatus);
  const docKindLabel =
    document.doc_kind && document.doc_kind !== 'other'
      ? document.doc_kind.replace(/_/g, ' ')
      : null;

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Select ${document.title || document.filename}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        transition: theme =>
          theme.transitions.create(['border-color', 'background-color'], {
            duration: theme.transitions.duration.short,
          }),
        '&:hover': {
          bgcolor: isSelected ? 'action.selected' : 'action.hover',
          borderColor: isSelected ? 'primary.main' : 'primary.light',
        },
      }}
    >
      {isProcessing ? (
        <CircularProgress size={10} sx={{ flexShrink: 0 }} />
      ) : (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: getStatusColor(effectiveStatus),
            flexShrink: 0,
          }}
        />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {showSkeleton ? (
          <Skeleton variant="text"  sx={{ width: "80%" }}/>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontWeight: isSelected ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {document.title || document.filename}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {[docKindLabel, formatShortDate(document.created_at)].filter(Boolean).join(' · ')}
        </Typography>
        {document.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            {document.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
